import { spacing, Text, TextArea } from '@scality/core-ui';
import { Box, CopyButton } from '@scality/core-ui/dist/next';
import type React from 'react';
import styled from 'styled-components';

interface CodeBlockProps {
  code: string;
  language?: string;
}

const SHELL_LANGUAGES = [
  'bash',
  'sh',
  'shell',
  'zsh',
  'console',
  'powershell',
  'pwsh',
  'fish',
  'cmd',
  'batch',
  'terminal',
];

const CodeBlockContainer = styled.div`
  border-radius: ${spacing.f8};
  overflow: hidden;
  margin: ${spacing.f16} 0;
  background-color: ${(props) => props.theme.backgroundLevel2};
  border: 1px solid ${(props) => props.theme.border};
  textarea {
    border: none;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    font-family: "Fira Code", monospace;
    padding: 0;
  }
  textarea:hover {
    border: 0;
  }
  textarea:focus {
    border: 0;
    outline: none;
  }
  textarea:focus-visible {
    border: 1px solid ${(props) => props.theme.selectedActive};
  }
`;

const CodeBlockHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.r1} ${spacing.r8};
  background-color: ${(props) => props.theme.backgroundLevel3};
  border-bottom: 1px solid ${(props) => props.theme.backgroundLevel3};
  font-size: 14px;
`;

const StyledText = styled(Text)`
  font-size: 14px;
  line-height: reset;
  font-weight: reset;
`;

const StyledTextAreaContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  padding: ${spacing.r8} ${spacing.r16};
  background-color: ${(props) => props.theme.backgroundLevel1};
`;

export const CodeBlock: React.FC<CodeBlockProps> = ({ code, language = 'text' }) => {
  const isShellCommand = SHELL_LANGUAGES.includes(language.toLowerCase());
  const displayLabel = isShellCommand ? 'Command' : language;

  return (
    <CodeBlockContainer>
      <CodeBlockHeader>
        <StyledText isGentleEmphazed color="textSecondary">
          {displayLabel}
        </StyledText>
        <CopyButton textToCopy={code} variant="ghost" />
      </CodeBlockHeader>
      <StyledTextAreaContainer>
        <TextArea value={code} variant="code" width="100%" readOnly autoGrow={true} />
      </StyledTextAreaContainer>
    </CodeBlockContainer>
  );
};
